/* eslint-disable array-callback-return */
/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable max-len */
/* eslint-disable no-plusplus */
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { customAlphabet, urlAlphabet } from 'nanoid';
import Papa from 'papaparse';
import nigerianStates from './nigerian-states.json';
import { formatCash } from './tableHelpers';
import { HomeApplication } from '../interfaces/homeApplication';

export const getGroupItems = (array, property) => array.reduce((groups, item) => {
  const name = item[property];
  // eslint-disable-next-line no-param-reassign
  const group = groups[name] || (groups[name] = []);
  group.push(item);
  return groups;
}, {});

export const getTimeStampDaysAgo = (days: number) => {
  const now = new Date();
  const daysAgo = new Date(now);
  daysAgo.setDate(now.getDate() - days);
  return daysAgo.getTime();
};

export const getRepaymentStatus = (dueDate: string, isFullyPaid: boolean) => {
  if (isFullyPaid) return 'Paid';
  // if due date is less than 2 days ago, return defaulting
  const now = new Date();
  const twoDaysAgo = new Date(now);
  twoDaysAgo.setDate(now.getDate() - 2);
  const dueDateObj = new Date(dueDate);
  if (dueDateObj <= twoDaysAgo) {
    return 'Defaulting';
  }
  // if it is greater than 2 days ago and less than or equal today return due
  if (dueDateObj > twoDaysAgo && dueDateObj <= now) {
    return 'Due';
  }
  // if it is greater than today return not due
  return 'Not Due';
};

const validateUrl = (url) => {
  const regex = /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png)/g;
  if (regex.test(url)) return url;
  return null;
};

export const toTitleCase = (str: string) => str.replace(
  /\w\S*/g,
  (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
);
export const truncateWords = (data) => (data?.length > 20 ? `${data?.substring(0, 20)}....` : data);

export const getBusinessInfo = ({
  businessTypes,
  industries,
  registrationTypes,
  categories,
}: {
  businessTypes?: [any];
  industries?: [any];
  registrationTypes?: [any];
  categories?: [any];
}) => {
  const out: {
    businessTypes?: any;
    industries?: any;
    registrationTypes?: any;
    categories?: any;
  } = {};
  if (businessTypes) {
    out.businessTypes = businessTypes?.map(
      ({ name, value }: { name: string; value: string }) => ({
        name,
        text: toTitleCase(name),
        value,
      }),
    );
  }

  if (industries) {
    out.industries = industries?.map(
      ({ name, code }: { name: string; code: string }) => ({
        name: toTitleCase(name),
        text: toTitleCase(name),
        value: code,
      }),
    );
  }

  if (registrationTypes) {
    out.registrationTypes = registrationTypes?.map(
      ({ name, code }: { name: string; code: string }) => ({
        name,
        text: toTitleCase(name),
        value: code,
      }),
    );
  }
  if (categories) {
    out.categories = categories?.map(
      ({ name, id }: { name: string; id: string }) => ({
        name,
        text: toTitleCase(name),
        value: id,
      }),
    );
  }
  return out;
};
export const capitalizeOnlyFirstLetter = (value) => value && value?.charAt(0).toUpperCase() + value?.slice?.(1);

export const getStates = () => {
  const state = Object.keys(nigerianStates || [])?.map?.((key: string) => ({
    text: key,
    value: key,
  }));
  return state;
};

export const getLocalGovt = (state: string) => {
  const allStates: any = nigerianStates;
  if (!allStates) return [];
  const stateRec = allStates[state];
  const lga = stateRec?.map?.((record: string) => ({
    text: record,
    value: record,
  }));
  return lga || '';
};

export const getNameInitials = (name: string) => {
  const nameSplits = name?.trim?.()?.split?.(' ');
  if (!nameSplits?.length) return '';
  let initials = '';
  for (let index = 0; index < nameSplits.length; index++) {
    const splittedName = nameSplits[index];
    initials += `${splittedName?.[0]?.toUpperCase?.() || ''}`;
  }

  return initials.slice(0, 1);
};

export const formatUserCanadianStatus = (status: string, replacements = []) => {
  if (!status) return '';
  const delimiter = status?.includes('_') ? '_' : ' ';
  const words = status.split(delimiter);

  let formattedStatus = words.join(' ');

  replacements.forEach(([wordToReplace, replacement]) => {
    if (formattedStatus === wordToReplace) {
      formattedStatus = replacement;
    }
  });

  return `${formattedStatus} status`;
};

export const getPhoneNum = (str: string | null | undefined): any => {
  const initials = str && str.slice(0, 4);
  if (initials === '+234' && str) {
    return `${str.slice(4, str.length)}`;
  }
  return str;
};

export const cleanUpBankDetails = (raw: any) => {
  if (!raw?.length) return [];
  const out = raw?.map(
    ({
      accountName,
      accountNumber,
      currency,
      bank,
      id,
      status,
      swiftCode,
    }: {
      accountName: string;
      status: any;
      accountNumber: string;
      currency: string;
      bank: any;
      id: string;
      swiftCode: string;
    }) => ({
      accountName,
      accountNumber,
      currency,
      bank: bank?.name,
      bankId: bank?.nipCode,
      swiftCode,
      id,
      enabled: status ? 'default' : 'inactive',
    }),
  );

  return out;
};

export const getKeys = (raw: any) => {
  const result = [];
  const publicKey = raw?.publicKey;
  const secretKey = raw?.secretKey;

  if (publicKey) {
    result.push({
      id: 1,
      name: 'Public Key',
      token: publicKey,
    });
  }
  if (secretKey) {
    result.push({
      id: 2,
      name: 'Secret Key',
      token: secretKey,
    });
  }
  return result;
};

export const cleanUpTeams = (raw: any) => {
  try {
    if (!raw?.length || !raw?.[0]?.firstName) {
      return [];
    }
    const out = raw?.map(
      ({
        firstName,
        lastName,
        email,
        role,
        id,
        createdAt,
        ...rest
      }: {
        firstName: string;
        status: any;
        lastName: string;
        email: string;
        role: any;
        id: string;
        createdAt: string;
        lastLogin: string;
        rest: any;
      }) => ({
        name: `${firstName} ${lastName}`,
        email,
        role,
        createdAt,
        id,
        firstName,
        lastName,
        ...rest,
        status: rest?.status ? 'Active' : 'inactive',
        date: rest?.lastLogin
          ? moment(new Date(Number(rest?.lastLogin))).format('MMM DD, YYYY')
          : 'N/A',
        time: Number(rest?.lastLogin)
          ? moment(new Date(Number(rest?.lastLogin))).format('hh:mm a')
          : '',
        roleId: role?.id,
      }),
    );
    return out;
  } catch (error) {
    return [];
  }
};

export const getRawRecord = () => [
  {
    id: 1,
    name: 'Albert Okpala',
    email: 'albert.okpala@gmail.com',
    role: 'Administrator',
    type: 'Orders',
    amount: 450000,
    quantity: 100,
    currency: 'CAD',
    lastLogin: 'Wednesday March 24, 2021',
  },
  {
    id: 2,
    name: 'Igho Matthew',
    email: 'igho.matthew@gmail.com',
    role: 'User',
    lastLogin: 'Wednesday March 24, 2021',
    type: 'Orders',
    amount: 450000,
    quantity: 100,
    currency: 'CAD',
  },
  {
    id: 3,
    name: 'Kelvin Esegbna',
    email: 'kevoesegbona@gmail.com',
    role: 'User',
    lastLogin: 'Wednesday March 24, 2021',
    type: 'Orders',
    amount: 3050000,
    quantity: 900,
    currency: 'CAD',
  },
];

export const cleanUpCustomersOverview = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    (
      {
        firstName,
        lastName,
        orders,
        createdAt,
        email,
        status,
        id,
        transactionAmount,
        payfiId,
        ...rest
      }: {
        id: string;
        firstName: string;
        email: string;
        lastName: string;
        orders: any;
        createdAt: string;
        status: string;
        transactionAmount: number;
        payfiId: string;
      },
      index,
    ) => ({
      id: id || index + 1,
      customerId: id,
      customerRef: payfiId,
      name: `${firstName} ${lastName}`,
      type: 'Total orders',
      image: null,
      quantity: orders,
      currency: 'CAD',
      email: email || 'name@test.com',
      date: moment(new Date(Number(createdAt))).format('MMM DD, YYYY'),
      time: moment(new Date(Number(createdAt))).format('hh:mm a'),
      dateTime: moment(new Date(Number(createdAt))).format(
        'MMM DD, YYYY hh:mm a',
      ),
      status: status ? 'Active' : 'Inactive',
      transactionAmount: formatCash(transactionAmount, '$ '),
      amountName: 'Total Amount',
      ...rest,
    }),
  );

  return out;
};

export const dateFormatter = (
  date: string | any,
  format: string,
  returnVal: string = '______',
) => {
  if (!date) return returnVal;
  if (new Date(date).toString() === 'Invalid Date') return returnVal;
  return moment(new Date(date)).format(format);
};

export const cleanSingleRentCollectionRecord = (rentPayment: any) => {
  if (!rentPayment) return rentPayment;
  return {
    ...rentPayment,
    rentAmount: rentPayment?.rentAmount
      ? `C$ ${formatCash(rentPayment?.rentAmount)}`
      : '***********',
    name: `${rentPayment?.user?.firstName || '******'} ${
      rentPayment?.user?.lastName || '******'
    }`,
    landLordName: rentPayment?.property
      ? `${rentPayment?.property?.landlord?.firstName || '**********'} ${
        rentPayment?.property?.landlord?.lastName || '**********'
      }`
      : `${rentPayment?.tenant?.landlord?.firstName} ${rentPayment?.tenant?.landlord?.lastName}`,
    landLordEmail: `${
      rentPayment?.property?.landlord_e_transfer_email
      || rentPayment?.property?.landlord?.email
      || rentPayment?.tenant?.landlord?.email
      || '**********'
    }`,
    landLordPhoneNumber: rentPayment?.property
      ? rentPayment?.property?.landlord?.phone
      : rentPayment?.tenant?.landlord?.phone || '**********',
    leaseFileDetails:
      rentPayment?.property?.meta?.leaseFileDetails || '**********',
    companyName: rentPayment?.property?.landlord?.companyName || '**********',
    email: rentPayment?.user?.email || '**********',
    id: rentPayment?.id,
    propertyAddress: rentPayment?.property
      ? rentPayment?.property?.propertyAddress
      : rentPayment?.tenant?.property?.rentalAddress || '**********',
    unitNumber: rentPayment?.property
      ? rentPayment?.property?.unitNumber
      : rentPayment?.tenant?.landlord?.noOfUnits || '**********',
    userId: rentPayment?.meta?.paymentLinkDetail?.data?.meta?.userId,
    landlordType: rentPayment?.property?.landlord?.landlordType,
    rentReportStatus:
      rentPayment?.user?.userRtDetail?.creditBuilderStatus || '**********',
  };
};

export const cleanSingleBillsPaymentRecord = (billsPayment: any) => {
  if (!billsPayment) return billsPayment;

  return {
    ...billsPayment,
    amount: billsPayment?.amount
      ? `C$ ${formatCash(billsPayment?.amount)}`
      : '***********',
    name: `${billsPayment?.user?.firstName || '******'} ${
      billsPayment?.user?.lastName || '******'
    }`,
    biller: billsPayment?.biller || '**********',
    billerCode: billsPayment?.billerCode || '**********',
    fee: billsPayment?.fee
      ? `C$ ${formatCash(billsPayment?.fee)}`
      : '**********',
    accountId: billsPayment?.accountId || '**********',
    createdAt: billsPayment?.createdAt
      ? moment(new Date(Number(billsPayment?.createdAt))).format('MMM DD, YYYY')
      : '**********',
    datePaid: billsPayment?.datePaid
      ? moment(new Date(Number(billsPayment?.datePaid))).format('MMM DD, YYYY')
      : '**********',
    updatedAt: billsPayment?.updatedAt
      ? moment(new Date(Number(billsPayment?.updatedAt))).format('MMM DD, YYYY')
      : '**********',
    status: billsPayment?.status || '**********',
    email: billsPayment?.user?.email || '**********',
    id: billsPayment?.id,
  };
};

export const cleanSingleMemberRecord = (members: any) => {
  if (!members) return members;
  return {
    ...members,
    department: members?.department || '**********',
    email: members?.email || '**********',
    name: `${members?.firstName || '******'} ${members?.lastName || '******'}`,
    phone: members?.phone || '**********',
    role: members?.role || null,
    roleName: members?.role?.name || '**********',
    roleId: members?.role?.id || '**********',
    status: members?.status || '',
    createdAt: members?.createdAt
      ? moment(new Date(members?.createdAt)).format('MMMM DD, YYYY hh:mm a')
      : '**********',
    lastLoggedInAt: members?.lastLoggedInAt
      ? moment(new Date(members?.lastLoggedInAt)).format(
        'MMMM DD, YYYY hh:mm a',
      )
      : '**********',
  };
};

export const cleanSingleCustomerRecord = (customer: any) => {
  if (!customer?.firstName) return customer;
  return {
    ...customer,
    bank: customer?.bankDetail?.bank?.name,
    accountNumber: customer?.bankDetail?.accountNumber,
    createdAt: dateFormatter(customer?.createdAt, 'Do, MMM YYYY', ''),
    dob: customer?.dob || '',
    ...(customer?.primaryAddress || {}),
    ...(customer?.employmentdetail || {}),
    nextOfKinName: `${customer?.nextofkin?.firstName || ''} ${
      customer?.nextofkin?.lastName || ''
    }`.trim(),
    nextOfKinPhone: customer?.nextofkin?.phoneNumber,
    nextOfKinEmail: customer?.nextofkin?.email,
    nextOfKinRelationship: customer?.nextofkin?.relationship,
    nextOfKinAddress: customer?.nextofkin?.address,
    status: customer?.status
      ? 'ACTIVE'
      : customer?.isDeleted
        ? 'DELETED'
        : 'INACTIVE',
    headerStatus: customer?.status
      ? 'Active'
      : customer?.isDeleted
        ? 'Deleted'
        : 'Inactive',
    userId: customer?.id,
    isNysc:
      customer?.employmentdetail?.employmentStatus?.toLowerCase?.()
      === 'nysc member',
    company:
      customer?.employmentdetail?.employmentStatus?.toLowerCase?.()
      === 'nysc member'
        ? 'NYSC'
        : 'Payfi',
    name: `${customer?.firstName} ${customer?.lastName}`,
    promise: formatCash(customer?.promise, '$ '),
    offer: formatCash(customer?.offer, '$ '),
    id: customer?.id,
  };
};

export const cleanSingleMerchantCustomerRecord = (customer: any) => {
  if (!customer?.id) return customer;
  return {
    ...customer,
    name:
      `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
      || 'N/A',
    isActive: !!customer?.status,
    customerStatus: customer?.status ? 'Active' : 'InActive',
    transactionAmount: formatCash(customer?.transactionAmount, '$ '),
  };
};

export const getOnboardigStage = (stage: string | number, isNysc) => {
  const payfiStage = [
    'Incomplete',
    'Employment Details',
    'Personal Details',
    'BVN Verification',
    'Debit Card',
    'Account Verification',
    'Identity Verification',
    'Completed',
  ];
  const nyscStage = [
    'Incomplete',
    'Employment Details',
    'Contact Details',
    'Identity Verification',
    'BVN Verification',
    'Bank Details',
    'Debit Card',
    'Account Verification',
    'Completed',
  ];
  const stages = isNysc ? nyscStage : payfiStage;
  return stages[stage];
};

export const getOnboardigStagesArr = (isNysc: boolean): string[] => {
  const payfiStage = [
    'Incomplete',
    'Employment Details',
    'Personal Details',
    'BVN Verification',
    'Debit Card',
    'Account Verification',
    'Identity Verification',
    'Completed',
  ];
  const nyscStage = [
    'Incomplete',
    'Employment Details',
    'Contact Details',
    'Identity Verification',
    'BVN Verification',
    'Bank Details',
    'Debit Card',
    'Account Verification',
    'Completed',
  ];
  const stages = isNysc ? nyscStage : payfiStage;
  return stages;
};

export const handleCleanUpOnboardingStatus = (value: any) => {
  switch (value) {
  case 'in_review':
    return 'Awaiting Approval';
  case 'ongoing':
    return 'Prospective';
  case 'approved':
    return 'Active';
  case 'in_review_rejected':
    return 'Inactive';
  default:
    return 'Prospective';
  }
};

export const handleCleanUpProductPreference = (value: any) => {
  switch (value) {
  case 'pre_rent_guarantee':
    return 'Pre-rent Guarantee';
  case 'rent_guarantee':
    return 'Rent Guarantee';
  case 'apartment_search':
    return 'Apartment Search';
  case 'rent_payment':
    return 'Rent Payment';
  default:
    return '**********';
  }
};

export const cleanUpAdminCustomers = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      firstName,
      lastName,
      orders,
      createdAt,
      email,
      lastLoginAt,
      payfiProduct,
      imageUrl,
      onboardingRoute,
      onBoardingStage,
      payfiId,
      status,
      userRtDetail,
      phone,
      ...rest
    }: {
      firstName: string;
      email: string;
      lastName: string;
      orders: any;
      createdAt: string;
      lastLoginAt: string;
      payfiProduct: any;
      source: string;
      stage: string;
      imageUrl: string;
      payfiId: string;
      onboardingRoute: string;
      onBoardingStage: string;
      approvedAt: string;
      status: string;
      userRtDetail: any;
      phone: string;
    }) => ({
      payfiId: payfiId || '**********',
      name: firstName ? `${firstName} ${lastName}` : '**********',
      type: 'Orders',
      image: imageUrl || null,
      quantity: orders,
      currency: 'CAD',
      email: email || '**********',
      date: createdAt
        ? moment(new Date(createdAt)).format('MMM DD, YYYY')
        : '**********',
      time: createdAt
        ? moment(new Date(createdAt)).format('hh:mm a')
        : '**********',
      status: handleCleanUpOnboardingStatus(userRtDetail?.onboardingStatus),
      lastLoginAt:
        lastLoginAt || createdAt
          ? dateFormatter(
            lastLoginAt || createdAt,
            'MMM DD, YYYY, hh:mm a',
            ' ',
          )
          : '**********', // moment(new Date(lastLoginAt)).format('MMM DD, YYYY'),
      lastLoginAtTime: dateFormatter(lastLoginAt || createdAt, 'hh:mm a', ' '), // moment(new Date(lastLoginAt)).format('hh:mm a'),
      source: payfiProduct?.name || '**********',
      defaultStatus: status,
      ...rest,
      onboardingRoute:
        {
          web: 'Web App',
          mobile: 'Mobile App',
          SDK: 'SDK',
        }[onboardingRoute] || '**********',
      stage: `${getOnboardigStage(
        onBoardingStage || 0,
        payfiProduct?.name?.toUpperCase?.() === 'NYSC',
      )}`,
      dateApproved:
        dateFormatter(rest?.approvedAt, 'MMM DD, YYYY', ' ')?.trim?.()
        || '**********',
      dateDeleted:
        dateFormatter(createdAt, 'MMM DD, YYYY', ' ')?.trim?.() || '**********',
      phone: phone || '**********',
      productPreference: handleCleanUpProductPreference(userRtDetail?.product),
    }),
  );

  return out;
};

export const cleanUpAdminAllStaff = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allAdminStaff {
    email: string;
    firstName: any;
    lastName: any;
    status: null | string;
    department: string;
    imageUrl: string;
    phone: string;
    role: {
      display_name: string;
      name: string;
      id: string;
    };
  }
  const out = raw?.map(
    ({
      email,
      firstName,
      lastName,
      role,
      phone,
      status,
      imageUrl,
      department,
      ...rest
    }: allAdminStaff) => ({
      ...rest,
      image: imageUrl || null,
      name: `${firstName || '*****'} ${lastName || '*****'}`,
      email: email || '**********',
      status: status || '**********',
      role:
        role?.display_name?.split('-').join(' ')
        || role?.name?.split('-').join(' ')
        || '**********',
      phone: phone || '**********',
      department: department || '**********',
      dataRoleId: role?.id,
      firstName,
      lastName,
    }),
  );

  return out;
};

export const cleanUpMerchants = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allMerchant {
    name: string;
    email: string;
    contactEmail: string;
    phone: string;
    id: string;
    logo: string;
    createdAt: string;
    isApproved: string;
    contactPhone: string;
    payfiId: string;
    declinedAt: string;
    approvedAt: string;
    lastDeactivatedAt: string;
    status: string;
    category: any;
  }
  const out = raw?.map(
    ({
      name,
      createdAt,
      logo,
      isApproved,
      payfiId,
      id,
      email,
      phone,
      declinedAt,
      approvedAt,
      lastDeactivatedAt,
      status,
      category,
      ...rest
    }: allMerchant) => ({
      ...rest,
      id,
      payfiId,
      name: addEllipsis(name || '*******', 20),
      type: 'Orders',
      image: validateUrl(logo),
      currency: 'CAD',
      date: moment(new Date(Number(createdAt))).format('MMM DD, YYYY'),
      time: moment(new Date(Number(createdAt))).format('hh:mm a'),
      dateRegistered: dateFormatter(
        createdAt && new Date(Number(createdAt)),
        'MMM DD, YYYY',
        '************',
      ),
      dateApproved: dateFormatter(
        approvedAt && new Date(Number(approvedAt)),
        'MMM DD, YYYY',
        '************',
      ),
      dateDeclined: dateFormatter(
        declinedAt && new Date(Number(declinedAt)),
        'MMM DD, YYYY',
        '************',
      ),
      dateDeactivated: dateFormatter(
        lastDeactivatedAt && new Date(Number(lastDeactivatedAt)),
        'MMM DD, YYYY',
        '************',
      ),
      status: isApproved ? 'Active' : 'Inactive',
      merchantStatus: status,
      contactEmail: email || '************',
      contactPhone: phone || '************',
      category: addEllipsis(category?.name || '************', 20),
    }),
  );

  return out;
};

export const cleanUpEquifaxReports = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      equifaxSubmittedByAdmin,
      createdAt,
      equifaxSubmittedAt,
      ...rest
    }: any) => ({
      ...rest,
      name: equifaxSubmittedByAdmin?.firstName
        ? `${equifaxSubmittedByAdmin?.firstName} ${equifaxSubmittedByAdmin?.lastName}`
        : 'N/A',
      email: equifaxSubmittedByAdmin?.email,
      month: rest?.monthGeneratedFor?.toUpperCase?.(),
      submissionStatus: rest?.equifaxSubmitted ? 'submitted' : 'not submitted',
      createdAt: dateFormatter(
        createdAt && new Date(createdAt),
        'MMM DD, YYYY',
        '************',
      ),
      equifaxSubmittedAt: dateFormatter(
        equifaxSubmittedAt && new Date(equifaxSubmittedAt),
        'MMM DD, YYYY',
        '************',
      ),
      // month:
    }),
  );

  return out;
};

export const cleanUpAdminCustomerDetailsTransaction = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allTransactions {
    id: string;
    txRef: string;
    createdAt: string;
    status: string;
    amount: any;
    fees: any;
    dueDate: any;
    message: string;
    type: string;
    description: string;
  }
  const out = raw?.map(
    ({
      createdAt,
      txRef,
      id,
      status,
      amount,
      fees,
      dueDate,
      description,
      message,
      ...rest
    }: allTransactions) => ({
      ...rest,
      id,
      txRef: txRef || '**********',
      currency: 'CAD',
      transactionDate: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '************',
      dateDisbutsed: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '************',
      createdAt: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '************',
      amount: amount || '$ 0',
      status: handleStatus(status),
      processFee: formatCash(fees || 0, '$ '),
      date: dueDate
        ? moment(dueDate).format('MMM DD, YYYY, hh:mm a')
        : '**********',
      message: message || '**********',
      description: description || '**********',
    }),
  );
  return out;
};

export const cleanUpAdminCustomerDetailsLoan = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allLoans {
    merchant: {
      name: string;
    };
    order: {
      items: string;
      orderItems: any;
    };
    loanStatus: {
      name: string;
    };
    id: string;
    txRef: string;
    createdAt: string;
    status: string;
    amount: any;
    fees: any;
    dueDate: any;
    payfiId: string;
    outstandingAmount: any;
    loanChannel: string;
  }
  const out = raw?.map(
    ({
      merchant,
      createdAt,
      txRef,
      order,
      id,
      amount,
      fees,
      dueDate,
      loanStatus,
      payfiId,
      outstandingAmount,
      loanChannel,
      ...rest
    }: allLoans) => ({
      ...rest,
      id,
      payfiId: payfiId || '**********',
      merchant: merchant?.name || '**********',
      txRef: txRef || '**********',
      currency: 'CAD',
      transactionDate: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '************',
      dateDisbutsed: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '************',
      createdAt: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '************',
      amount: amount || '$ 0',
      item: order?.items || '**********',
      status: loanStatus?.name,
      processFee: formatCash(fees || 0, '$ '),
      date: dueDate
        ? moment(dueDate).format('MMM DD, YYYY, hh:mm a')
        : '**********',
      loanStatus: loanStatus?.name,
      outstandingAmount: formatCash(outstandingAmount || 0, '$ '),
      loanChannel: capitalizeEverFirstLetter(
        loanChannel?.split('_').join(' ') || '**********',
      ),
    }),
  );
  return out;
};

export const cleanUpUserCaTransactionsRec = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  interface allUserCaTransactionsRec {
    id: string;
    description: string;
    narration?: string;
    status: string;
    createdAt?: string;
    amount?: number;
  }

  const out = raw?.map(
    ({
      id,
      description,
      // narration,
      amount,
      status,
      createdAt,
      ...rest
    }: allUserCaTransactionsRec) => ({
      ...rest,
      id,
      transactions: description || '**********',
      type: 'Orders',
      status: handleStatus(status),
      amount: amount ? `$ ${formatCash(amount)}` : '**********',
      date: createdAt
        ? moment(createdAt).format('MMM DD, YYYY, hh:mm a')
        : '**********',
    }),
  );
  return out;
};

export const cleanUpAdminCustomerDetailsRepayment = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allRepayments {
    merchant: {
      name: string;
    };
    user: {
      email: string;
      lastName: string;
      firstName: string;
      phone: string;
      id: string;
    };
    order: {
      orderItems: any;
    };
    loan: {
      tenor: string;
      paymentChannel: string;
      user: {
        email: string;
        lastName: string;
        firstName: string;
        phone: string;
        id: string;
        imageUrl: string;
      };
      loanStatus: {
        name: string;
      };
      transactions: {
        paymentMethod: string;
      };
    };
    transactions?: {
      id: string;
      paymentMethod: string;
      status: string;
      createdAt: string;
      updatedAt: string;
      paidAt: string;
    }[];
    amountDue: any;
    originalAmount: any;
    loanId: string;
    id: string;
    payDate: string;
    charges: any;
    dueDate: any;
    instalments: any;
    interest: any;
    isFullyPaid: boolean;
  }
  const out = raw?.map(
    ({
      merchant,
      order,
      id,
      loanId,
      originalAmount,
      amountDue,
      dueDate,
      loan,
      transactions,
      // payDate,
      interest,
      instalments,
      isFullyPaid,
      ...rest
    }: allRepayments) => {
      const customerName = `${loan?.user?.firstName || '**********'} ${
        loan?.user?.lastName || ''
      }`;
      return {
        ...rest,
        id,
        merchant: merchant?.name || '**********',
        type: 'Orders',
        loanId: loanId || '**********',
        name: customerName,
        email: loan?.user?.email || '**********',
        image: loan?.user?.imageUrl || '**********',
        phone: loan?.user?.phone || '**********',
        currency: 'CAD',
        originalAmount: formatCash(originalAmount || 0, '$ ') || '$ 0',
        amountDue: formatCash(amountDue || 0, '$ ') || '$ 0',
        item: order?.orderItems?.length
          ? order?.orderItems?.map((list) => `${list?.orderName},`)
          : '**********',
        interest: interest || 0,
        defaultCharges: formatCash(
          originalAmount && amountDue ? amountDue - originalAmount : 0,
          '$ ',
        ),
        instalments: instalments ? `${instalments} Instalments` : '**********',
        repaymentdueDate: dueDate
          ? moment(dueDate).format('MMM DD, YYYY')
          : '**********',
        tenor: loan?.tenor,
        status: getRepaymentStatus(dueDate, isFullyPaid),
        paymentChannel: loan?.paymentChannel,
        paymentMethod: transactions?.[0]?.paymentMethod || '**********',
        payDate:
          transactions?.[0]?.updatedAt || transactions?.[0]?.createdAt
            ? moment(
              transactions?.[0]?.updatedAt || transactions?.[0]?.paidAt,
            ).format('MMM DD, YYYY, hh:mm a')
            : '**********',
      };
    },
  );
  return out;
};

export const cleanUpAdminAllSettlements = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allAdminSettlements {
    merchant: {
      name: string;
      logo: string;
    };
    totalAmount: any;
    id: string;
    logo: string;
    createdAt: string;
    status: string;
    settledOn: string;
    totalInterest: any;
    totalSettled: any;
    type: string;
    txRef: string;
    amount: any;
    vendorFees: any;
  }
  const out = raw?.map(
    ({
      merchant,
      createdAt,
      settledOn,
      id,
      totalAmount,
      status,
      totalInterest,
      vendorFees,
      type,
      amount,
      ...rest
    }: allAdminSettlements) => ({
      ...rest,
      id,
      name: addEllipsis(merchant?.name || '**********', 20),
      type: type || '**********',
      image: validateUrl(merchant?.logo),
      currency: 'CAD',
      settledOn: settledOn
        ? moment(new Date(Number(settledOn))).format('MMM DD, YYYY, hh:mm a')
        : '**********',
      time: moment(new Date(Number(createdAt))).format('hh:mm a'),
      createdAt: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
        )
        : '************',
      status,
      totalAmount: formatCash(totalAmount || 0, '$ '),
      totalInterest: formatCash(totalInterest || 0, '$ '),
      totalSettled: formatCash(
        (totalAmount || 0) - (vendorFees || 0) || 0,
        '$ ',
      ),
      amount: amount || '**********',
    }),
  );

  return out;
};
export const cleanUpAdminAllSettlementsTransaction = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allAdminSettlementsTransaction {
    merchant: {
      name: string;
      logo: string;
    };
    totalAmount: any;
    id: string;
    logo: string;
    createdAt: string;
    status: string;
    paidAt: string;
    totalInterest: any;
    totalSettled: any;
    type: string;
    txRef: string;
    amount: any;
    vendorFees: any;
    amountPaid: any;
    channel: string;
    user: any;
  }
  const out = raw?.map(
    ({
      merchant,
      createdAt,
      paidAt,
      id,
      totalAmount,
      status,
      totalInterest,
      vendorFees,
      type,
      amount,
      amountPaid,
      channel,
      ...rest
    }: allAdminSettlementsTransaction) => ({
      ...rest,
      id,
      name: merchant?.name,
      customer: `${rest?.user?.firstName || '**********'} ${
        rest?.user?.lastName || ''
      }`,
      type: type || '**********',
      image: validateUrl(merchant?.logo),
      currency: 'CAD',
      paidAt: paidAt
        ? moment(new Date(Number(paidAt))).format('MMM DD, YYYY, hh:mm a')
        : '**********',
      time: moment(new Date(Number(createdAt))).format('hh:mm a'),
      createdAt: createdAt
        ? moment(new Date(Number(createdAt))).format('MMM DD, YYYY')
        : '************',
      status,
      totalAmount: formatCash(totalAmount || 0, '$ '),
      totalInterest: formatCash(totalInterest || 0, '$ '),
      totalSettled: formatCash(
        (amountPaid || amount || 0) - (vendorFees || 0) || 0,
        '$ ',
      ),
      amount: amount || '**********',
      channel: channel || '**********',
    }),
  );

  return out;
};

const getDocInfo = (docArr) => {
  if (!docArr?.length) {
    return {
      approvedDoc: 'No Approved Doc(s)',
      rejectedDoc: 'No Rejected Doc(s)',
      submittedDoc: 'No Submitted Doc(s)',
      pendingDoc: 'No Pending Doc(s)',
    };
  }
  const DocObj = {
    approvedDoc: 0,
    rejectedDoc: 0,
    submittedDoc: 0,
    pendingDoc: 0,
  };

  docArr.forEach(({ status }: { status: string }) => {
    if (Number(status) === -1) {
      // rejected
      DocObj.rejectedDoc += 1;
    } else if (Number(status) === 1) {
      // rejected
      DocObj.approvedDoc += 1;
    } else {
      DocObj.pendingDoc += 1;
    }
    DocObj.submittedDoc += 1;
  });

  return {
    approvedDoc: `${DocObj?.approvedDoc || 'No'} Approved Doc(s)`,
    rejectedDoc: `${DocObj?.rejectedDoc || 'No'} Rejected Doc(s)`,
    submittedDoc: `${DocObj?.submittedDoc || 'No'} Submitted Doc(s)`,
    pendingDoc: `${DocObj?.pendingDoc || 'No'} Pending Doc(s)`,
  };
};

export const cleanUpAdminKYC = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allMerchant {
    name: string;
    email: string;
    phone: string;
    id: string;
    logo: string;
    createdAt: string;
    isApproved: string;
    documents: any;
  }
  const out = raw?.map(
    ({
      name,
      createdAt,
      logo,
      isApproved,
      id,
      email,
      documents,
      ...rest
    }: allMerchant) => ({
      ...rest,
      id,
      name,
      image: validateUrl(logo),
      currency: 'CAD',
      date: moment(new Date(Number(createdAt))).format('MMM DD, YYYY'),
      time: moment(new Date(Number(createdAt))).format('hh:mm a'),
      status: isApproved ? 'Active' : 'Inactive',
      contactEmail: email || 'N/A',
      ...getDocInfo(documents),
    }),
  );

  return out;
};

export const cleanUpLoans = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allLoans {
    loanAmount: number;
    id: string;
    currency: string;
    createdAt: string;
    user: any;
    loanStatus: any;
    property: any;
    interest: any;
    downPayment: number;
    tenor: any;
    moratoriumDuration: number;
    meta: any;
  }
  const out = raw?.map(
    ({
      createdAt,
      property,
      interest,
      loanAmount,
      moratoriumDuration,
      meta,
      tenor,
      downPayment,
      ...rest
    }: allLoans) => ({
      currency: 'CAD',
      time: moment(createdAt).format('hh:mm a'),
      amount: formatCash(loanAmount, '$ '),
      tenor: tenor || '**********',
      name:
        `${rest?.user?.firstName || ''} ${rest?.user?.lastName || ''}`.trim()
        || '**********',
      email: rest?.user?.email || 'N/A',
      landlord:
        `${property?.landlord?.firstName || ''} ${
          property?.landlord?.lastName || ''
        }`.trim() || '**********',
      interest,
      rentAmount: meta?.rentAmount ? formatCash(meta?.rentAmount, '$ ') : 'N/A',
      property: property?.propertyAddress || '**********',
      ...rest,
      status:
        rest?.loanStatus?.name.split('_').join(' ') || rest?.loanStatus?.name,
      moratoriumDuration: `${moratoriumDuration} months`,
      downPayment: formatCash(downPayment, '$ '),
      image: rest?.user?.imageUrl || null,
    }),
  );
  return out;
};

export const cleanUpGuarantees = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allGuarantees {
    amount: number;
    annualIncome: number;
    id: string;
    currency: string;
    createdAt: string;
    user: any;
    property: any;
    status: string;
    meta: any;
    tenor: string;
  }
  const out = raw?.map(
    ({
      createdAt,
      property,
      amount,
      annualIncome,
      meta,
      tenor,
      ...rest
    }: allGuarantees) => ({
      currency: 'CAD',
      time: moment(createdAt).format('hh:mm a'),
      dateApplied: moment(createdAt).format('MMM DD, YYYY'),
      dateActioned: meta.approvedAt
        ? moment(meta.approvedAt).format('MMM DD, YYYY')
        : meta.declinedAt
          ? moment(meta.declinedAt).format('MMM DD, YYYY')
          : 'N/A',
      datePaid: `${
        meta?.paidAt ? moment(meta?.paidAt).format('MMM DD, YYYY') : 'N/A'
      }`,
      amount: formatCash(amount, '$ '),
      annualIncome: formatCash(annualIncome, '$ '),
      name:
        `${rest?.user?.firstName || ''} ${rest?.user?.lastName || ''}`.trim()
        || '**********',
      email: rest?.user?.email || 'N/A',
      landlord:
        property?.landlord?.companyName
        || `${property?.landlord?.firstName || ''} ${
          property?.landlord?.lastName || ''
        }`.trim()
        || '**********',
      property: property?.propertyAddress || '**********',
      monthlyBudget: formatCash(meta?.monthlyBudget, '$ '),
      leasePeriod: `${tenor} ${getMonthPluralization(tenor)}`,
      apartmentType: capitalizeEverFirstLetter(meta?.apartmentType),
      ...rest,
      status: rest?.status?.split('_').join(' ') || rest?.status,
      image: rest?.user?.imageUrl || undefined,
    }),
  );
  return out;
};

export const cleanUpComment = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  const out = raw?.map(
    ({
      comment,
      action,
      admin,
      createdAt,
      ...rest
    }: {
      comment: string;
      admin: {
        firstName: string;
        lastName: string;
        email: string;
      };
      email: any;
      action: string;
      createdAt: string;
    }) => ({
      ...rest,
      admin: `${admin?.firstName} ${admin?.lastName}`,
      email: admin?.email,
      action,
      comment: `${comment}`,
      createdAtDate: createdAt
        ? dateFormatter(createdAt, 'MMM DD, YYYY hh:mm a', '**********')
        : '**********',
    }),
  );

  return out;
};

export const cleanUpSDKActivity = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  const out = raw?.map(
    ({
      sessionMeta,
      payfiId,
      merchant,
      createdAt,
      partner,
      user,
      narration,
      amount,
      channel,
      ...rest
    }: {
      comment: string;
      disbursementStatus: string;
      merchant: {
        name: string;
        logo: string;
      };
      user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        payfiProduct: any;
      };
      userActivity: {
        action: string;
      };
      disbursementMessage: string;
      sessionMeta: any;
      payfiId: string;
      createdAt: string;
      status: string;
      partner: any;
      sdkorderItems: { orderName: string }[];
      stage: string;
      narration: string;
      amount: any;
      channel: string;
    }) => ({
      ...rest,
      merchant: merchant?.name,
      logo: merchant?.logo,
      customer: `${`${user?.firstName || '*****'} ${
        user?.lastName || '*****'
      }`}`,
      email: user?.email,
      userId: user?.id,
      payfiId,
      sessionMeta,
      partnerName: partner?.name || '**********',
      createdAtDate: createdAt
        ? dateFormatter(createdAt, 'MMM DD, YYYY hh:mm a', '**********')
        : '**********',
      narration: narration || '**********',
      amount: amount || '**********',
      channel: channel || '**********',
      stage: rest?.userActivity?.[0]?.screen || '**********',
      disbursementMessage: rest?.disbursementMessage?.trim?.() || '**********',
      status: rest?.status || 'N/A',
    }),
  );
  return out;
};

const handleStatus = (status: string) => {
  if (!status) return 'FAILED';
  const activeStatuses = ['paid', 'success', 'active', 'successful'];
  const pendingStatus = ['pending', 'initiated'];
  if (activeStatuses?.includes(status?.toLowerCase?.())) {
    return 'SUCCESSFUL';
  }
  if (pendingStatus?.includes(status?.toLowerCase?.())) {
    return 'PENDING';
  }
  return 'FAILED';
};

export const cleanUpTransactions = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  const out = raw?.map(
    ({
      createdAt,
      txRef,
      firstName,
      lastName,
      amount,
      email,
      ...rest
    }: {
      email: string;
      createdAt: string;
      txRef: any;
      firstName: any;
      lastName: any;
      amount: any;
      status: null | string;
      fees: number;
    }) => ({
      date: moment(createdAt).format('MMM DD, YYYY'),
      time: moment(createdAt).format('hh:mm a'),
      currency: 'CAD',
      ...rest,
      createdAt,
      customer: `${firstName} ${lastName}`,
      email: email || 'N/A',
      trxAmount: amount,
      txRef: txRef || 'N/A',
      charges: formatCash(rest?.fees || 0, '$'),
      status: handleStatus(rest?.status),
      dateTime: moment(createdAt).format('MMM DD, YYYY hh:mm a'),
    }),
  );

  return out;
};

export const cleanUpAdminTransactions = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allTransaction {
    email: string;
    createdAt: string;
    txRef: any;
    firstName: any;
    lastName: any;
    amount: any;
    status: null | string;
    user?: any;
    merchant?: any;
    paymentRef?: string;
    paymentMethod?: string;
    equityContribution?: string;
    channel?: string;
    message?: string;
    transactionType?: string;
    fees: number;
    loan: any;
  }
  const out = raw?.map(
    ({
      createdAt,
      txRef,
      amount,
      status,
      paymentRef,
      merchant,
      ...rest
    }: allTransaction) => ({
      date: moment(new Date(createdAt)).format('MMM DD, YYYY'),
      time: moment(new Date(createdAt)).format('hh:mm a'),
      dateTime: moment(new Date(createdAt)).format('MMM DD, YYYY hh:mm a'),
      currency: 'CAD',
      name: `${rest?.user?.firstName || '*****'} ${
        rest?.user?.lastName || '*****'
      }`,
      createdAt,
      email: rest?.user?.email || '**********',
      trxAmount: formatCash(Number(amount) || 0, '$'),
      txRef: txRef || paymentRef || '**********',
      status: handleStatus(status),
      ...rest,
      merchant: merchant?.name || '**********',
      merchantEmail: merchant?.email || '**********',
      message: rest?.message || '**********',
      paymentMethod: rest?.paymentMethod?.trim?.() || '**********',
      amount: formatCash(Number(amount) || 0, '$'),
      fees: formatCash(Number(rest?.fees) || 0, '$ '),
      image: rest?.user?.imageUrl || null,
    }),
  );
  return out;
};

export const cleanUpBillsPayment = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  interface allTransaction {
    email: string;
    createdAt: string;
    amount: any;
    status: null | string;
    user?: any;
    fees: number;
    biller: string;
  }
  const out = raw?.map(
    ({
      createdAt, amount, status, biller, ...rest
    }: allTransaction) => ({
      createdAt: createdAt
        ? moment(new Date(Number(createdAt))).format('MMM DD, YYYY, hh:mm a')
        : '**********',
      time: moment(new Date(createdAt)).format('hh:mm a'),
      dateTime: moment(new Date(createdAt)).format('MMM DD, YYYY hh:mm a'),
      currency: 'CAD',
      name: `${rest?.user?.firstName || '*****'} ${
        rest?.user?.lastName || '*****'
      }`,
      email: rest?.user?.email || '**********',
      biller: biller || '**********',
      status: handleStatus(status),
      ...rest,
      amount: formatCash(Number(amount) || 0, '$'),
      fees: formatCash(Number(rest?.fees) || 0, '$ '),
      image: rest?.user?.imageUrl || null,
    }),
  );
  return out;
};

export const cleanUpRealtorsDetail = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      firstName,
      lastName,
      createdAt,
      realtors,
      selfie,
      numberOfProperties,
      numberOfTenants,
      ...rest
    }: {
      firstName: string;
      lastName: string;
      createdAt: string;
      selfie: string;
      numberOfProperties: any;
      numberOfTenants: any;
      realtors: {
        id: string;
        email: string;
        status: any;
      };
    }) => ({
      id: realtors?.id,
      name: firstName ? `${firstName} ${lastName}` : '**********',
      image: selfie || null,
      email: realtors?.email || '**********',
      dateOnboarded: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
          '************',
        )
        : '********',
      status: realtors?.status || '**********',
      numberOfProperties: numberOfProperties || 0,
      numberOfTenants: numberOfTenants || 0,
      ...rest,
    }),
  );
  return out;
};

export const cleanUpRewards = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      createdAt,
      user,
      earnings,
      totalReferred,
      completedReferrals,
      ...rest
    }: {
      firstName: string;
      email: string;
      lastName: string;
      createdAt: string;
      imageUrl: string;
      status: string;
      earnings: number;
      totalReferred: number;
      completedReferrals: number;
      user: {
        firstName: string;
        lastName: string;
        email: string;
        imageUrl: string;
        primaryAddress: {
          location: string;
        };
        userRtDetail: {
          enrollment_type: string;
          onboardingStatus: string;
        };
        status: any;
      };
    }) => ({
      name: user?.firstName
        ? `${user?.firstName} ${user?.lastName}`
        : '**********',
      image: user?.imageUrl || null,
      location: user?.primaryAddress?.location || '**********',
      email: user?.email || '**********',
      earnings: `$${formatCash(earnings)}`,
      totalReferred: totalReferred || '0',
      completedReferrals: completedReferrals || '0',
      enrollmentType: user?.userRtDetail?.enrollment_type
        ? capitalizeEverFirstLetter(user?.userRtDetail?.enrollment_type)
          ?.split('_')
          .join(' ')
        : '**********',
      date: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
          '************',
        )
        : '**********',
      // time: createdAt ? moment(new Date(createdAt)).format('hh:mm a') : '**********',
      status: user?.userRtDetail?.onboardingStatus
        ? handleCleanUpOnboardingStatus(user?.userRtDetail?.onboardingStatus)
        : '',

      ...rest,
    }),
  );

  return out;
};

export const cleanUpRealtorCode = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      createdAt,
      firstName,
      code,
      email,
      lastName,
      totalRealtors,
      imageUrl,
      ...rest
    }: {
      firstName: string;
      email: string;
      lastName: string;
      createdAt: string;
      status: string;
      totalRealtors: string;
      code: string;
      imageUrl: string;
    }) => ({
      name: firstName ? `${firstName} ${lastName}` : '**********',
      image: imageUrl || null,
      email: email || '**********',
      code: code || '**********',
      totalRealtors: totalRealtors || '**********',
      date: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
          '************',
        )
        : '**********',
      // time: createdAt ? moment(new Date(createdAt)).format('hh:mm a') : '**********',
      // eslint-disable-next-line no-restricted-globals
      status,

      ...rest,
    }),
  );

  return out;
};

export const handleRentPaymentStatus = (value: any) => {
  switch (value) {
  case 'settled':
    return 'Settled';
  case 'refunded':
    return 'Refunded';
  default:
    return 'Awaiting';
  }
};

export const handleRentReporting = (value: any) => {
  switch (value) {
  case 'INTERESTED':
    return 'Yes';
  default:
    return 'No';
  }
};

export const cleanUpRentPayments = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      createdAt,
      settlementStatus,
      user,
      rentAmount,
      actionDate,
      status,
      meta,
      equifaxSubmitted,
      ...rest
    }: {
      createdAt: string;
      status: string;
      settlementStatus: string;
      rentAmount: number;
      actionDate: string;
      meta: any;
      equifaxSubmitted: boolean;
      user: {
        firstName: string;
        lastName: string;
        imageUrl: string;
        email: string;
        userRtDetail: any;
      };
    }) => ({
      name: user?.firstName
        ? `${user?.firstName} ${user?.lastName}`
        : '**********',
      email: user?.email || '**********',
      image: user?.imageUrl || null,
      amountPaid: rentAmount ? `$${formatCash(rentAmount)}` : '**********',
      date:
        meta?.paidAt || createdAt
          ? dateFormatter(
            meta?.paidAt || createdAt,
            'MMM DD, YYYY, hh:mm A',
            'N/A',
          )
          : '**********',
      dateActioned: actionDate
        ? dateFormatter(actionDate, 'MMM DD, YYYY, hh:mm A', 'N/A')
        : '**********',
      ...rest,
      status: handleRentPaymentStatus(settlementStatus) || '**********',
      paymentStatus: status?.toLowerCase?.() || '**********',
      rentReporting: user?.userRtDetail
        ? handleRentReporting(user?.userRtDetail?.creditBuilderStatus)
        : '**********',
      equifaxSubmitted: equifaxSubmitted ? 'Reported' : 'Not Reported',
      // payDate: transactions?.[0]?.updatedAt || transactions?.[0]?.createdAt ? moment(transactions?.[0]?.updatedAt || transactions?.[0]?.paidAt).format('MMM DD, YYYY, hh:mm a') : '**********',
    }),
  );

  return out;
};

export const cleanUpRewardDetails = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      createdAt,
      referred,
      earning,
      totalReferred,
      completedReferrals,
      ...rest
    }: {
      firstName: string;
      email: string;
      lastName: string;
      createdAt: string;
      imageUrl: string;
      status: string;
      earning: number;
      totalReferred: number;
      completedReferrals: number;
      referred: {
        firstName: string;
        lastName: string;
        email: string;
        imageUrl: string;
        primaryAddress: {
          location: string;
        };
        userRtDetail: {
          enrollment_type: string;
          onboardingStatus: string;
        };
        status: any;
      };
    }) => ({
      name: referred?.firstName
        ? `${referred?.firstName} ${referred?.lastName}`
        : '**********',
      image: referred?.imageUrl || null,
      location: referred?.primaryAddress?.location || '**********',
      email: referred?.email || '**********',
      earning: `$${formatCash(earning)}`,
      enrollmentType: referred?.userRtDetail?.enrollment_type
        ? capitalizeEverFirstLetter(referred?.userRtDetail?.enrollment_type)
          ?.split('_')
          .join(' ')
        : '**********',
      totalReferred: totalReferred || '0',
      completedReferrals: completedReferrals || '0',
      date: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
          '************',
        )
        : '********',
      // time: createdAt ? moment(new Date(createdAt)).format('hh:mm a') : '**********',
      status: referred?.status || '**********',
      onboardingStatus: referred?.userRtDetail?.onboardingStatus
        ? handleCleanUpOnboardingStatus(
          referred?.userRtDetail?.onboardingStatus,
        )
        : '**********',
      ...rest,
    }),
  );
  return out;
};

export const getOrdinalForNumber = (num: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const cleanUpLoanSchedules = (raw: any) => {
  if (!raw?.length) {
    return [];
  }
  // order by date
  let out = raw?.sort((a, b) => {
    const aDate = new Date(a.dueDate);
    const bDate = new Date(b.dueDate);
    return aDate.getTime() - bDate.getTime();
  });
  out = out?.map(
    (
      {
        dueDate,
        amountDue,
        isFullyPaid,
      }: {
        dueDate: string;
        amountDue: number;
        isFullyPaid: boolean;
      },
      n: number,
    ) => ({
      name: `${getOrdinalNumber(n + 1)} Settlement`,
      dueDate: dateFormatter(dueDate, 'MMM DD, YYYY', 'N/A'),
      amountDue: formatCash(amountDue, '$'),
      isFullyPaid,
      status: getRepaymentStatus(dueDate, isFullyPaid),
    }),
  );

  return out;
};

// const splitCamelCaseToWord = (str: string)
// => str.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

export const sanitizeFilter = (formValues: any) => {
  let out: any = {};
  const convert = {
    userEmail: 'email',
    userPhone: 'phone',
  };

  Object.keys(formValues)?.forEach((k) => {
    if (['userEmail', 'userPhone'].includes(k)) {
      if (out?.user) {
        out.user[convert[k]] = formValues[k];
      } else {
        out = {
          ...out,
          user: {
            [convert[k]]: formValues[k],
          },
        };
      }
    } else if (['fromDate', 'toDate'].includes(k)) {
      out[k] = formValues[k]
        ? new Date(formValues[k]).toISOString()
        : formValues[k];
    } else if (['isApproved'].includes(k)) {
      out[k] = formValues[k] === 'true';
    } else if (['minAmount', 'maxAmount', 'tenor'].includes(k)) {
      // eslint-disable-next-line radix
      out[k] = formValues[k] ? parseInt(formValues[k]) : null;
    } else {
      out[k] = formValues[k];
    }
  });
  return out;
};

export const parseJSON = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return {};
  }
};

export const cleanUpSingleTransaction = (raw: any) => {
  if (!raw) {
    return null;
  }
  const {
    createdAt,
    txRef,
    amount,
    paidAt,
    firstName,
    lastName,
    ...rest
  }: {
    email: string;
    createdAt: string;
    txRef: any;
    paidAt: any;
    amount: any;
    firstName: string;
    lastName: string;
    fees: number;
    shippingAddress?: string;
    productDetails?: any;
  } = raw;
  const name = `${firstName || ''} ${lastName || ''}`;
  const out = {
    customerName: name?.trim() || 'N/A',
    paidDate: moment(new Date(Number(paidAt))).format('MMM DD, YYYY'),
    paidTime: moment(new Date(Number(paidAt))).format('hh:mm a'),
    createdDate: moment(new Date(createdAt)).format('Do MMM, YYYY'),
    createdTime: moment(new Date(createdAt)).format('hh:mm a'),
    ...rest,
    trxAmount: formatCash(amount, '$'),
    txRef: txRef || 'N/A',
    fees: formatCash(rest?.fees || 0, '$'),
    amount: formatCash(amount + (rest?.fees || 0), '$'),
    shippingAddress: rest?.shippingAddress,
    productDetails: rest?.productDetails,
  };
  return out;
};

export const cleanUpAdminSingleTransaction = (raw: any) => {
  if (!raw?.amount) {
    return null;
  }

  const {
    createdAt,
    txRef,
    amount,
    status,
    paymentMethod,
    fees,
    user,
    loan,
    rentGuarantee,
    rentPayment,
    ...rest
  }: {
    email: string;
    createdAt: string;
    txRef: any;
    paidAt: any;
    amount: any;
    status: string;
    fees: any;
    type: string;
    paymentMethod: string;
    message: string;
    loan: any;
    rentGuarantee: any;
    rentPayment: any;
    user: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      status: any;
      imageUrl: string;
      id: string;
      userRtDetail: any;
      primaryAddress: any;
    };
  } = raw;
  const fullName = user?.firstName
    ? `${user?.firstName} ${user?.lastName}`
    : '**********';

  const getLandlordNameOrCompany = (property: any) => {
    const landlord = property?.landlord;
    if (landlord) {
      const fullName = `${landlord?.firstName || ''} ${
        landlord?.lastName || ''
      }`.trim();
      return fullName || toTitleCase(landlord?.companyName) || '**********';
    }
    return '**********';
  };

  const out = {
    paidDate: moment(new Date(Number(createdAt))).format('MMM DD, YYYY'),
    paidTime: moment(new Date(Number(createdAt))).format('hh:mm a'),
    createdDate: moment(new Date(createdAt)).format('MMM DD, YYYY hh:mm a'),
    createdTime: moment(new Date(createdAt)).format('hh:mm a'),
    captureDate: moment(new Date(createdAt)).format('MMM DD, YYYY hh:mm a'),
    captureTime: moment(new Date(createdAt)).format('hh:mm a'),
    ...rest,
    currency: 'CAD',
    trxAmount: amount,
    userId: user?.id,
    txRef: txRef || '**********',
    fees: formatCash(fees || 0, 'C$'),
    amount: formatCash(amount, 'C$'),
    subTotal: formatCash(amount - (fees || 0), 'C$'),
    name: fullName?.trim() || '**********',
    email: user?.email || '**********',
    status: status === 'success' ? 'successful' : status,
    phone: user?.phone || '**********',
    firstName: user?.firstName || '**********',
    lastName: user?.lastName || '**********',
    imageUrl: user?.imageUrl,
    userStatus: user?.status === 1 ? 'ACTIVE' : 'INACTIVE',
    landlord: loan
      ? getLandlordNameOrCompany(loan.property)
      : rentGuarantee
        ? getLandlordNameOrCompany(rentGuarantee.property)
        : rentPayment
          ? getLandlordNameOrCompany(rentPayment.property)
          : '**********',
    propertyAddress:
      loan?.property?.propertyAddress
      || rentPayment?.property?.propertyAddress
      || rentGuarantee?.property?.propertyAddress
      || '**********',
    property:
      loan?.property || rentPayment?.property || rentGuarantee?.property,
    paymentMethod: paymentMethod || '**********',
    rentAmount: formatCash(
      loan?.property?.rentAmount
        || rentPayment?.property?.rentAmount
        || rentGuarantee?.property?.rentAmount
        || 0,
      'C$',
    ),
    leaseFileDetails:
      loan?.property?.meta?.leasFileDetails
      || rentPayment?.property?.meta?.leaseFileDetails
      || rentGuarantee?.property?.leaseFileDetails
      || 'N/A',
    zipCode: user?.primaryAddress?.zipCode || 'N/A',
    campus: user?.userRtDetail?.campus || 'N/A',
    city: user?.primaryAddress?.city || 'N/A',
    state: user?.userRtDetail?.state || 'N/A',
    userRtDetail: user?.userRtDetail,
    loan,
    user,
  };

  return out;
};

export const cleanUpAdminSingleLoanRecord = (raw: any) => {
  if (!raw?.id) {
    return null;
  }

  const {
    id,
    createdAt,
    user,
    property,
    interest,
    loanStatus,
    loanAmount,
    loanStartDate,
    moratoriumDuration,
    meta,
    tenor,
    downPayment,
    loanSchedules,
    ...rest
  }: {
    loanAmount: number;
    loanStartDate: string;
    id: string;
    currency: string;
    createdAt: string;
    user: any;
    loanStatus: any;
    property: any;
    interest: any;
    downPayment: number;
    tenor: any;
    moratoriumDuration: number;
    meta: any;
    loanSchedules: any;
    hasFirstAndLastMonthPayment: boolean;
  } = raw;
  const name = user?.firstName ? `${user?.firstName} ${user?.lastName}` : null;
  const startDate = loanStartDate;
  const endDate = loanSchedules?.length
    ? loanSchedules?.[(loanSchedules?.length || 0) - 1]?.dueDate
    : null;
  const totalLoanScheduleAmount = loanSchedules?.reduce(
    (acc, cur) => acc + cur?.amountDue,
    0,
  );
  const out = {
    id,
    imageUrl: user?.imageUrl,
    promise: formatCash(user?.promise || 0, '$'),
    createdDate: moment(new Date(createdAt)).format('MMM DD, YYYY'),
    createdTime: moment(new Date(createdAt)).format('hh:mm a'),
    captureDate: moment(new Date(createdAt)).format('MMM DD, YYYY'),
    captureTime: moment(new Date(createdAt)).format('hh:mm a'),
    ...rest,
    tenor: `${tenor} Months`,
    loanSchedules:
      ['AWAITING_APPROVAL', 'AWAITING_PAYMENT'].includes(loanStatus?.name)
      && meta?.schedulesPreviewBeforeApproval
        ? meta?.schedulesPreviewBeforeApproval
        : loanSchedules,
    currency: 'CAD',
    trxAmount: formatCash(loanAmount || 0, '$ '),
    refNo: id || 'N/A',
    name: name?.trim() || 'N/A',
    email: user?.email,
    status: loanStatus?.name,
    property,
    userId: user?.id,
    phone: user?.phone,
    firstName: user?.firstName,
    lastName: user?.lastName,
    userStatus: user?.userStatus === 1 ? 'ACTIVE' : 'INACTIVE',
    interest: `${interest}%`,
    paymentCard: `**** ${user?.activeCard?.last4digits}`,
    landlord:
      `${property?.landlord?.firstName || ''} ${
        property?.landlord?.lastName || ''
      }`.trim() || '**********',
    landlordPhone: property?.landlord?.phone || '**********',
    hasFirstAndLastMonthPaid: rest?.hasFirstAndLastMonthPayment ? 'Yes' : 'No',
    startDate: startDate
      ? moment(new Date(startDate)).format('MMM DD YYYY')
      : 'N/A',
    endDate: endDate ? moment(new Date(endDate)).format('MMM DD, YYYY') : 'N/A',
    rentAmount: meta?.rentAmount ? formatCash(meta?.rentAmount, '$ ') : 'N/A',
    loanAmount: formatCash(totalLoanScheduleAmount, '$'),
    propertyAddress: property?.propertyAddress || '**********',
    leaseAgreementDoc: property?.leaseAgreementDoc,
    moratoriumDuration: `${moratoriumDuration} months`,
    downPayment: formatCash(downPayment, '$ '),
    leaseStartDate: moment(new Date(property?.leaseStartDate)).format(
      'MMM DD, YYYY',
    ),
    userRtDetail: user?.userRtDetail,
  };
  return out;
};

export const cleanUpAdminSingleGuaranteeRecord = (raw: any) => {
  if (!raw?.id) {
    return null;
  }

  const {
    id,
    createdAt,
    user,
    property,
    amount,
    annualIncome,
    guaranteeEndDate,
    guaranteeStartDate,
    guaranteeType,
    meta,
    tenor,
    ...rest
  }: {
    amount: number;
    loanStartDate: string;
    id: string;
    currency: string;
    createdAt: string;
    user: any;
    loanStatus: any;
    property: any;
    meta: any;
    status: string;
    guaranteeStartDate: string;
    guaranteeEndDate: string;
    guaranteeType: string;
    annualIncome: number;
    monthlyRent: number;
    tenor: string;
  } = raw;
  const name = user?.firstName ? `${user?.firstName} ${user?.lastName}` : null;

  const out = {
    id,
    imageUrl: user?.imageUrl,
    createdDate: moment(new Date(createdAt)).format('MMM DD, YYYY'),
    createdTime: moment(new Date(createdAt)).format('hh:mm a'),
    captureDate: moment(new Date(createdAt)).format('MMM DD, YYYY'),
    captureTime: moment(new Date(createdAt)).format('hh:mm a'),
    ...rest,
    currency: 'CAD',
    trxAmount: formatCash(amount || 0, 'C$ '),
    refNo: id || 'N/A',
    name: name?.trim() || 'N/A',
    email: user?.email,
    property,
    userId: user?.id,
    phone: user?.phone,
    firstName: user?.firstName,
    lastName: user?.lastName,
    userStatus: user?.userStatus === 1 ? 'ACTIVE' : 'INACTIVE',
    landlord:
      property?.landlord?.companyName
      || `${property?.landlord?.firstName || ''} ${
        property?.landlord?.lastName || ''
      }`.trim()
      || '**********',
    landlordPhone: property?.landlord?.phone || '**********',
    startDate: guaranteeStartDate
      ? moment(new Date(guaranteeStartDate)).format('MMM DD YYYY')
      : 'N/A',
    endDate: guaranteeEndDate
      ? moment(new Date(guaranteeEndDate)).format('MMM DD, YYYY')
      : 'N/A',
    annualIncome: formatCash(annualIncome, 'C$ '),
    amount: formatCash(amount, 'C$'),
    propertyAddress: property?.propertyAddress || '**********',
    leaseAgreementDoc: property?.leaseAgreementDoc,
    leaseStartDate: moment(new Date(property?.leaseStartDate)).format(
      'MMM DD, YYYY',
    ),
    payfiTotalFee:
      meta?.guaranteeFee && meta?.processingFee
        ? formatCash(meta?.guaranteeFee + meta?.processingFee, '$ ')
        : 'N/A',
    firstAndLastMonthPayment: formatCash(rest?.monthlyRent * 2, 'C$ '),
    monthlyRent: formatCash(rest?.monthlyRent, 'C$ '),
    guaranteeType,
    monthlyBudget: formatCash(meta?.monthlyBudget, 'C$ '),
    apartmentType: capitalizeEverFirstLetter(meta?.apartmentType),
    tenor: `${tenor} ${getMonthPluralization(tenor)}`,
    userRtDetail: user?.userRtDetail,
  };
  return out;
};

export const handleConvertStatus = (currentStatus: string) => {
  if (currentStatus === 'submitted') {
    return 'pending';
  }
  return currentStatus;
};

export const cleanUpHomeApplications = (raw = []) => {
  if (!raw.length) {
    return [];
  }

  const out = raw.map((props: HomeApplication) => ({
    ...props,
    currency: 'CAD',
    city: props.city,
    dateApplied: props.meta?.dateApplied
      ? moment(props.meta?.dateApplied).format('MMM DD, YYYY')
      : '********',
    dateCompleted: props.meta.completionDate
      ? moment(props.meta.completionDate).format('MMM DD, YYYY')
      : '********',
    datePaid: '********',
    amount: formatCash(props.meta.feePaid, '$ '),
    name:
      `${props.user?.firstName || ''} ${props?.user?.lastName || ''}`.trim()
      || '**********',
    email: props?.user?.email || '********',
    apartmentType: capitalizeEverFirstLetter(props.apartmentType),
    status:
      handleConvertStatus(props?.status)?.split('_').join(' ')
      || handleConvertStatus(props?.status),
    image: props?.user?.imageUrl || undefined,
  }));
  return out;
};

export const cleanUpSingleHomeApplication = (raw: any) => {
  if (!raw?.id) {
    return null;
  }
  const { id, user, ...rest }: HomeApplication = raw;
  const name = user?.firstName ? `${user?.firstName} ${user?.lastName}` : null;

  const out = {
    id,
    imageUrl: user?.imageUrl,
    ...rest,
    currency: 'CAD',
    refNo: id || '********',
    name: name?.trim() || '********',
    email: user?.email,
    userId: user?.id,
    phone: user?.phone,
    firstName: user?.firstName,
    lastName: user?.lastName,
    userStatus: user?.status === 1 ? 'ACTIVE' : 'INACTIVE',
    userRtDetail: user?.userRtDetail,
  };
  return out;
};

export const cleanUpProductPreference = (raw = []) => {
  if (!raw.length) {
    return [];
  }

  const out = raw?.map(
    ({
      firstName,
      lastName,
      email,
      imageUrl,
      payfiId,
      status,
      userRtDetail,
      ...rest
    }: {
      firstName: string;
      email: string;
      lastName: string;
      orders: any;
      createdAt: string;
      lastLoginAt: string;
      payfiProduct: any;
      source: string;
      stage: string;
      imageUrl: string;
      payfiId: string;
      onboardingRoute: string;
      onBoardingStage: string;
      approvedAt: string;
      status: string;
      userRtDetail: any;
      phone: string;
    }) => ({
      payfiId: payfiId || '**********',
      name: firstName ? `${firstName} ${lastName}` : '**********',
      image: imageUrl || null,
      currency: 'CAD',
      email: email || '**********',
      status: handleCleanUpOnboardingStatus(userRtDetail?.onboardingStatus),
      defaultStatus: status,
      rent_payment: userRtDetail?.meta?.onboardingProductStatus?.rent_payment,
      currentStatus: userRtDetail?.meta?.onboardingProductStatus?.currentStatus,
      rent_guarantee:
        userRtDetail?.meta?.onboardingProductStatus?.rent_guarantee,
      apartment_search:
        userRtDetail?.meta?.onboardingProductStatus?.apartment_search,
      pre_rent_guarantee:
        userRtDetail?.meta?.onboardingProductStatus?.pre_rent_guarantee,
      ...rest,
    }),
  );
  return out;
};

export const getColor = (name) => {
  const letters = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];
  const firstLetter = name[0];
  const pos = letters.indexOf(firstLetter) % 3;
  const availableColors = ['#FA5B7C', '#00C48C', '#5941A9'];

  return availableColors[Math.abs(pos)];
};

export const isAdmin = () => process.env.REACT_APP_TYPE === 'ADMIN';

export const baseRealtorUrl = process.env.REACT_APP_ENV === 'development'
  ? 'https://landlord-staging.payfi.ca/get-started'
  : 'https://landlord.payfi.ca/get-started';

export const defaultImageUrl = 'https://publicpayfiimages.s3.amazonaws.com/765-default-avatar.png';

export const getOrdinalNumber = (number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const index = number % 100 > 3 && number % 100 < 21 ? 0 : number % 10;
  const suffix = suffixes[index];
  return number + suffix;
};

export const checkExpiredDate = (date) => {
  const today = new Date();
  const expiryDate = new Date(date);
  return expiryDate < today;
};

export const processDocStatus = (status) => {
  switch (status) {
  case '-1':
    return 'rejected';
  case '1':
    return 'approved';
  default:
    return 'pending';
  }
};

export const convertUrlToHTTPS = (url) => {
  if (!url) return url;
  const httpsUrl = url.replace('http://', 'https://');
  return httpsUrl;
};

export const generateRandomCustomString = () => {
  const nanoid = customAlphabet(urlAlphabet, 10);
  return nanoid();
};

const nameTag = {
  cacDoc: 'CAC_DOCUMENT',
  certificateOfIncorporation: 'CERTIFICATE_OF_INCORPORATION_DOCUMENT',
  memorandum: 'MEMORANDUM_DOCUMENT',
  proofOfAddress: 'PROOF_OF_ADDRESS_DOCUMENT',
  identityDocument: 'OWNER_IDENTITY_DOCUMENT',
};

export const getDoc = (
  name:
    | 'cacDoc'
    | 'certificateOfIncorporation'
    | 'memorandum'
    | 'proofOfAddress'
    | 'identityDocument',
  arr: any,
) => {
  if (arr?.length) {
    const found = arr?.find(
      (ele: { documentType: string }) => ele?.documentType === nameTag[name],
    );
    return {
      file: {
        name: found?.documentType,
        url: found?.uri,
        docKey: found?.docKey,
        fileName: found?.fileName,
      },
    };
  }
};
export const mailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const compareobject = (obj1, obj2) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const eachKey in obj1) {
    if (obj1[eachKey] !== obj2[eachKey]) {
      return false;
    }
  }
  return true;
};
export const setItemsToLocalStorage = (name, data) => {
  localStorage.setItem(name, JSON.stringify(data));
};
export const getItemsToLocalStorage = (name) => {
  const actualData = JSON.parse(localStorage.getItem(name));
  return actualData;
};

export const compareStringify = (obj1, obj2) => {
  try {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  } catch (error) {
    return false;
  }
};

export const addHttpsToLinks = (link: any) => {
  const hasHttps = link.substring(0, 8);
  const hasHttp = link.substring(0, 7);
  if (!link) {
    return '';
  }
  if (hasHttps === 'https://' || hasHttp === 'http://') {
    return link;
  }

  if (link && (hasHttps !== 'https://' || hasHttp !== 'http://')) {
    return `https://${link}`;
  }
};

export const dateisDue = (dateRaw: string) => {
  const date = new Date(dateRaw);
  // check if date is valid
  if (date?.toString?.() === 'Invalid Date') {
    return false;
  }
  if (date.getTime() < new Date().getTime()) {
    return true;
  }
};

export const isLoanScheduleDueForDebit = (
  loanSchedule: any,
  loan: any,
): string | undefined => {
  const dueDate = loanSchedule?.dueDate;
  const isDue = dateisDue(dueDate);
  const isFullyPaid = loanSchedule?.isFullyPaid;
  if (
    isDue
    && !isFullyPaid
    && ['APPROVED', 'ONGOING'].includes(loan?.status?.toUpperCase?.())
    && ['PAYFI', 'CDL'].includes(loan?.paymentChannel?.toUpperCase?.())
  ) {
    return loanSchedule?.id;
  }
  return undefined;
};

export const useImperativeQuery = (query) => {
  const { refetch } = useQuery(query, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: true,
  });

  const imperativelyCallQuery = (variables) => refetch(variables);

  return imperativelyCallQuery;
};

export const addEllipsis = (str: string, maxLength: number = 40) => {
  if (str?.length > maxLength) {
    return `${str.substring(0, maxLength)}...`;
  }
  return str;
};

export const getAge = (date) => {
  const today = new Date();
  const birthDate = new Date(date);
  const age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1;
  }
  return age;
};

export const validateDateOfBirth = (dateOfBirth: string) => {
  // must be a valid date
  if (typeof dateOfBirth !== 'string') return false;
  const date = new Date(dateOfBirth);
  if (date?.toString?.() === 'Invalid Date') {
    return false;
  }
  // must be at least 18 years old
  const age = getAge(date);
  if (age < 21) {
    return false;
  }
  return true;
};

export const cleanColumnArray = (columns: any) => columns?.map?.((ele) => {
  if (Array.isArray(ele)) {
    return ele?.join('__');
  }
  return ele;
});

export const documentNameTag: any = {
  cacDoc: 'CAC_DOCUMENT',
  certificateOfIncorporation: 'CERTIFICATE_OF_INCORPORATION',
  memorandumAndArticlesOfAssociation: 'MEMORANDUM_AND_ARTICLES_OF_ASSOCIATION',
  SCUMLCertificate: 'SCUML_CERTIFICATE',
  CACStatusReport: 'CAC_STATUS_REPORT',
  certificateOfBusinessName: 'CERTIFICATE_OF_BUSINESS_NAME',
  proofOfAddress: 'PROOF_OF_ADDRESS_DOCUMENT',
};

export const businessDocuments = (businessType: string, kycDocs) => {
  const IncorporatedDocumentTypes = [
    {
      name: 'certificateOfIncorporation',
      label: 'Certificate Of Incorporation',
      filename: documentNameTag.certificateOfIncorporation,
      fileInp: kycDocs?.certificateOfIncorporation,
    },
    {
      name: 'memorandumAndArticlesOfAssociation',
      label: 'Memorandum And Articles Of Association',
      filename: documentNameTag.memorandumAndArticlesOfAssociation,
      fileInp: kycDocs?.memorandumAndArticlesOfAssociation,
    },
    {
      name: 'SCUMLCertificate',
      label: 'SCUML Certificate',
      filename: documentNameTag.SCUMLCertificate,
      fileInp: kycDocs?.SCUMLCertificate,
      isOptional: true,
    },
    {
      name: 'CACStatusReport',
      label: 'CAC Status Report',
      filename: documentNameTag.CACStatusReport,
      fileInp: kycDocs?.CACStatusReport,
      isOptional: true,
    },
  ];

  const registeredBusinessDocumentTypes = [
    {
      name: 'certificateOfBusinessName',
      label: 'Certificate Of BusinessName',
      filename: documentNameTag.certificateOfBusinessName,
      fileInp: kycDocs?.certificateOfBusinessName,
    },
    {
      name: 'CACStatusReport',
      label: 'CAC Status Report',
      filename: documentNameTag.CACStatusReport,
      fileInp: kycDocs?.CACStatusReport,
      isOptional: true,
    },
    {
      name: 'SCUMLCertificate',
      label: 'SCUML Certificate',
      filename: documentNameTag.SCUMLCertificate,
      fileInp: kycDocs?.SCUMLCertificate,
      isOptional: true,
    },
  ];

  if (businessType?.toLowerCase?.() === 'incorporated') {
    return IncorporatedDocumentTypes;
  }
  if (businessType?.toLowerCase?.() === 'registered') {
    return registeredBusinessDocumentTypes;
  }

  return [];
};

export const getOtherDocuments = (kycDocs) => {
  const allowed = [
    'CAC_DOCUMENT',
    'CERTIFICATE_OF_INCORPORATION',
    'MEMORANDUM_AND_ARTICLES_OF_ASSOCIATION',
    'SCUML_CERTIFICATE',
    'CAC_STATUS_REPORT',
    'CERTIFICATE_OF_BUSINESS_NAME',
  ];
  const others = kycDocs?.filter?.(
    (each) => !allowed.includes(each?.documentType?.toUpperCase?.()),
  );
  return others;
};

export const getDocByName = (name: string, arr: any) => {
  if (arr?.length) {
    const found = arr?.find(
      (ele: { documentType: string }) => ele?.documentType === documentNameTag[name],
    );
    return {
      file: {
        name: found?.documentType,
        url: found?.uri,
        docKey: found?.docKey,
        fileName: found?.fileName,
      },
      ...found,
    };
  }
};

export const documentIsValid = (businessType: string, kycDocs) => {
  const records = businessDocuments(businessType, kycDocs);
  if (!records?.length) return false;
  const isValid = records.every((ele) => {
    if (ele.isOptional) return true;
    return ele?.fileInp?.file?.name;
  });
  return isValid;
};
export const MaskCharacter = (str, mask, n = 1) => {
  const getStr = str.toString();
  return (
    `${getStr}`.slice(0, 4)
    + `${getStr}`.slice(4, -n).replace(/./g, mask)
    + `${getStr}`.slice(-n)
  );
};
export const capitalizeFirstLetter = (string) => string?.charAt(1)?.toUpperCase() + string?.substring(2)?.toLowerCase();
export const capitalizeEverFirstLetter = (string) => string?.replace(
  /(^\w|\s\w)(\S*)/g,
  (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase(),
);
export const addStyleToEvenElements = (n, className) => {
  switch (n % 2 !== 0) {
  case true:
    return className;
  default:
    return '';
  }
};
export const SplitName = (data?: string, position?: any) => {
  if (!position) {
    return data?.substring(0, data?.indexOf(' '));
  }
  if (position) {
    return data?.substring(data?.indexOf(' ') + position);
  }
};

// remove +234 or 234 from phone number
export const removeCountryCode = (phone: string) => {
  if (phone?.startsWith('+234')) {
    return phone?.replace('+234', '');
  }
  if (phone?.startsWith('234')) {
    return phone?.replace('234', '');
  }
  if (phone?.startsWith('0')) {
    return phone?.replace('0', '');
  }
  return phone;
};

export const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const getUserActivityStatusAndStyle = (name, activityStatus) => {
  let status = 'Not available';
  let statusStyle = 'style-not-available';
  if (name?.toLowerCase?.() === 'launch sdk') {
    status = 'Completed';
    statusStyle = 'style-completed';
  } else if (activityStatus?.toLowerCase?.() === 'success') {
    status = 'Completed';
    statusStyle = 'style-completed';
  } else if (activityStatus?.toLowerCase?.() === 'failing') {
    status = 'Failed';
    statusStyle = 'style-failed';
  }

  return {
    status,
    statusStyle,
  };
};

export const convertSpacesToHyphen = (str) => str?.trim?.().replace(/\s+/g, '-');
export const covertArrayToString = (str = []) => {
  const getText = str?.map((list: any) => list);
  return getText?.toString();
};

export const adminSideBarPermissionsAndRoutes = [
  {
    link: '/customers?page=1',
    name: 'Customers',
    permission: [
      'can-view-all-customers',
      'can-view-approved-customers',
      'can-view-prospective-customers',
      'can-view-priority-customers',
      'can-view-declined-customers',
      'can-view-deleted-customers',
      'can-view-product-analysis',
    ],
  },
  {
    link: '/transactions?page=1',
    name: 'Transactions',
    permission: [
      'can-view-all-transactions',
      'can-view-all-transaction-tab',
      'can-view-pending-transaction',
      'can-view-successful-transaction',
      'can-view-failed-transaction',
      'can-view-all-wallet-transaction',
      'can-view-wallet-pending-transaction',
      'can-view-successful-wallet-transaction',
      'can-view-failed-wallet-transaction',
    ],
  },
  {
    link: '/housing-applications',
    name: 'Housing Applications',
    permission: [
      'can-update-apartment-search-status',
      'can-view-apartment-detail',
    ],
  },
  {
    link: '/repayments?page=1',
    name: 'Repayments',
    permission: [
      'can-get-repayments',
      'can-get-repayment-by-id',
      'can-view-due-repayments',
      'can-view-upcoming-repayments',
      'can-view-defaulting-repayments',
      'can-view-paid-repayments',
    ],
  },
  {
    link: '/bills-payment?page=1',
    name: 'Bills Payment',
    permission: ['can-get-bills-payment'],
  },
  {
    link: '/audit-logs',
    name: 'Audit Logs',
    permission: 'can-get-audit-trails',
  },
  {
    link: '/settings',
    name: 'Settings',
  },
];

export const getRouteAdminCanGoBasedOnPermissions = (permissions: any) => {
  const adminPermissions = permissions?.map?.((each) => each?.name);
  if (!adminPermissions?.length) return '/settings';

  const allowedRoutes = adminSideBarPermissionsAndRoutes?.filter?.(
    (each: any) => {
      const permissionIsArray = Array?.isArray(each?.permission);
      if (permissionIsArray) {
        return each?.permission?.find((list: any) => adminPermissions?.includes?.(list));
      }
      return adminPermissions?.includes?.(each?.permission);

      // if (each?.permission) return adminPermissions?.includes?.(each?.permission);
      // // console.log('retrun active route', permissionWithArray(each?.permission, adminPermissions));
      // return true;
    },
  );

  return allowedRoutes?.[0]?.link || '/settings';
};

export const cleanSelectOverviewWithPermission = (
  permissions,
  customerFilterPermission,
) => {
  const newArray = [];
  customerFilterPermission?.map((listKey: any) => {
    const letsFindIfPermissionExist = permissions?.find(
      (permissionKey: any) => permissionKey?.name === listKey?.permission,
    );
    if (letsFindIfPermissionExist) {
      newArray?.push({
        ...letsFindIfPermissionExist,
        defaultSelect: listKey?.defaultSelect,
      });
    }
  });
  return newArray[0]?.defaultSelect || 'total';
};

export const adminHasPermission = (permissions, permission) => {
  const adminPermissions = permissions?.map?.((each) => each?.name);
  if (!adminPermissions?.length) return true;
  return adminPermissions?.includes?.(permission);
};

export const convertCsvFileToJSON = (file: any) => new Promise((resolve) => {
  // using papa parse to convert csv file to json
  Papa.parse(file, {
    header: true,
    complete: (results) => {
      resolve(results?.data);
    },
  });
});

export const processVoucherCodes = async (file: any) => {
  const fileData: any = await convertCsvFileToJSON(file);
  let voucherKeyToUse = '';
  Object.keys(fileData?.[0])?.forEach((key) => {
    if (key?.toLowerCase?.()?.includes?.('vouchers')) {
      voucherKeyToUse = key;
    }
  });
  if (!voucherKeyToUse) {
    return {
      error:
        'Invalid csv file format. File should contain the column "vouchers"',
    };
  }

  // check for duplicate vouchers
  const voucherDuplicateMap = {};
  let duplicateCounts = 0;
  const voucherRecords = [];

  for (let index = 0; index < fileData.length; index++) {
    const obj = fileData[index];
    const voucherCode = obj?.[voucherKeyToUse]?.trim?.();
    if (voucherCode) {
      if (voucherDuplicateMap[voucherCode]) {
        voucherDuplicateMap[voucherCode] += 1;
        duplicateCounts += 1;
      } else {
        voucherDuplicateMap[voucherCode] = 1;
        voucherRecords.push(voucherCode);
      }
    }
  }
  if (!voucherRecords?.length) {
    return {
      error: 'No valid voucher code found in the file',
    };
  }
  return {
    result: {
      duplicate: duplicateCounts,
      new: voucherRecords?.length,
      vouchers: voucherRecords,
    },
  };
};

export const addPhoneCountryCode = (phone: string) => {
  const prefixCode = phone?.substr(0, 4);
  const appendCode = phone?.substr(1, phone?.length);

  if (prefixCode === '+234') {
    return phone;
  }
  return phone ? `+234${appendCode}` : '';
};

export const phoneNumberValidator = (str) => {
  if (str.match(/^\+?[0-9]\d{1,20}$/)) {
    return true;
  }
  return false;
};

export const getMonthPluralization = (value) => {
  let loanTenorStringAttach;
  if (value < 1) {
    loanTenorStringAttach = '';
  } else if (value < 2) {
    loanTenorStringAttach = 'month';
  } else {
    loanTenorStringAttach = 'months';
  }
  return loanTenorStringAttach;
};

export const returnNumber = (num: string | number, fallBack) => {
  const result = Number.isNaN(Number(num)) ? fallBack : Number(num);
  return result;
};

export const cleanUpPartnerCode = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      createdAt,
      partnerName,
      code,
      email,
      totalReferredUsers,
      imageUrl,
      ...rest
    }: {
      partnerName: string;
      email: string;
      createdAt: string;
      status: string;
      totalReferredUsers: string;
      code: string;
      imageUrl: string;
    }) => ({
      name: partnerName || '**********',
      image: imageUrl || null,
      email: email || '**********',
      code: code || '**********',
      totalReferrals: totalReferredUsers || '**********',
      date: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
          '************',
        )
        : '**********',
      // eslint-disable-next-line no-restricted-globals
      status,

      ...rest,
    }),
  );

  return out;
};

export const cleanUpPartnerReferredUsersDetail = (raw: any) => {
  if (!raw?.length) {
    return [];
  }

  const out = raw?.map(
    ({
      createdAt,
      firstName,
      lastName,
      email,
      userRtDetail,
      tenants,
      rentPayments,
      ...rest
    }: {
      firstName: string;
      lastName: string;
      email: string;
      createdAt: string;
      userRtDetail: any;
      tenants: any[];
      rentPayments: any[];
    }) => ({
      name: `${firstName || ''} ${lastName || ''}`.trim() || '**********',
      email: email || '**********',
      image: null, // No selfie field available in schema
      institutionName:
        userRtDetail?.institution_name || userRtDetail?.company_name || 'N/A',
      enrollmentType: userRtDetail?.enrollment_type || 'N/A',
      numberOfTenants: tenants?.length || 0,
      totalRentPayments: rentPayments?.length || 0,
      dateOnboarded: createdAt
        ? dateFormatter(
          createdAt && new Date(Number(createdAt)),
          'MMM DD, YYYY',
          '************',
        )
        : '**********',
      ...rest,
    }),
  );

  return out;
};

export const cleanUpKaaboCoupons = (raw: any[]) => {
  if (!raw?.length) {
    return [];
  }
  return raw.map((row: any) => {
    const exp = row.expiryDate ? new Date(row.expiryDate) : null;
    const expiredByDate = exp ? exp.getTime() < Date.now() : false;
    const discountLabel = row.discountType === 'percentage'
      ? `${row.discountValue ?? 0}%`
      : `$${row.discountValue ?? 0}`;
    return {
      ...row,
      partnerLabel: row.partner?.partnerName || '—',
      expiryLabel: exp ? dateFormatter(exp, 'MMM DD, YYYY h:mmA', '—') : '—',
      discountLabel,
      usesLabel: `${row.currentUses ?? 0} / ${row.maxUses ?? 0}`,
      isExpired: expiredByDate || row.status === 'expired',
      statusDisplay: expiredByDate && row.status === 'active' ? 'expired' : row.status,
    };
  });
};

export const basePartnerUrl = process.env.REACT_APP_ENV === 'development'
  ? 'https://landlord-dev.kaabo.ai/get-started'
  : 'https://landlord.kaabo.ai/get-started';
